import { t } from 'i18n';
import React, { useEffect, useState } from 'react';
import { SearchBar as SearchBarInput } from '@teko/ui';
import { useSearchSuggestions } from 'hooks';
import { usePopularKeyword } from 'contexts';
import { RecommendedKeyword } from './RecommendedKeyword';
import { useRouter } from 'next/router';
import { SearchRecent } from './SearchRecent';
import { SearchList } from './SearchList';
import { ARROW_KEY_DOWN, ARROW_KEY_UP } from 'constants/index';
import { DiscoveryKeyword } from '@teko/discovery';
import { trackManually } from '@teko/tracking';
import { styled } from '@teko/ds-desktop';
const SearchBarWrapper = styled.div`
  position: relative;
`;
const StyledSearchBar = styled(SearchBarInput)`
  border: 1px solid #eaeaea;
`;
const SearchSuggestion = styled.div`
  position: absolute;
  background: #fff;
  width: 120%;
  border-radius: 8px;
  box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0.8rem;
  z-index: 1000;
  display: ${(props: { show: boolean }): string =>
    props.show ? 'block' : 'none'};
`;
export const SearchBar: React.FC = () => {
  const router = useRouter();
  const [visible, setVisible] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<number | null>(
    null,
  );
  const {
    keyword,
    setKeyword,
    histories,
    suggestions,
    removeSearchHistories,
    onSearch,
    isSuggestProduct,
    onRedirectDetailProduct,
    addToCart,
  } = useSearchSuggestions();
  const { terms, setStartCallApi } = usePopularKeyword();
  const [input, setInput] = useState<string>(keyword || '');
  const showHistories = !keyword?.trim() && histories.length > 0;
  const showSuggestions = keyword?.trim() && suggestions.length > 0;
  const showRecommendedKeywords = !keyword?.trim() && terms.length > 0;
  const handleSearch = (keywordObj: DiscoveryKeyword) => {
    onSearch(keywordObj);
    setVisible(false);
  };
  const onKeyUp = (e: React.KeyboardEvent): void => {
    if (e.keyCode === 13) {
      if (!visible || selectedSuggestion === null) {
        !!input?.trim() &&
          handleSearch({
            query: input.trim(),
          });
        return;
      }
      if (showHistories || (showSuggestions && !isSuggestProduct)) {
        const searchParam = showHistories
          ? histories[selectedSuggestion]
          : suggestions[selectedSuggestion];
        handleSearch(searchParam);
        return;
      }
      if (showSuggestions && isSuggestProduct) {
        onRedirectDetailProduct(suggestions[selectedSuggestion]?.sku);
        return;
      }
    }
  };
  const handleNavigateSearchResult = (ev: React.KeyboardEvent): void => {
    if (
      visible &&
      (ev.keyCode === ARROW_KEY_UP || ev.keyCode === ARROW_KEY_DOWN)
    ) {
      ev.preventDefault();
      if (!showSuggestions && !showHistories) return;
      const currentList = showHistories ? histories : suggestions;
      const length = currentList.length;
      if (selectedSuggestion === null) {
        setSelectedSuggestion(0);
        setInput(currentList[0].query || '');
      } else {
        if (ev.keyCode === ARROW_KEY_UP) {
          if (selectedSuggestion <= 0) {
            setSelectedSuggestion(length - 1);
            setInput(currentList[length - 1].query || '');
          } else {
            setSelectedSuggestion((prev) => {
              if (prev === null) return null;
              setInput(currentList[prev - 1].query || '');
              return prev - 1;
            });
          }
        } else if (ev.keyCode === ARROW_KEY_DOWN) {
          if (selectedSuggestion >= length - 1) {
            setSelectedSuggestion(0);
            setInput(currentList[0].query || '');
          } else {
            setSelectedSuggestion((prev) => {
              if (prev === null) return null;
              setInput(currentList[prev + 1].query || '');
              return prev + 1;
            });
          }
        }
      }
    }
  };
  useEffect(() => {
    const k = router?.query?.query?.toString();
    setSelectedSuggestion(null);
    const inputKeyword = k || '';
    setInput(inputKeyword);
    setKeyword(inputKeyword);
  }, [router]);
  return (
    <SearchBarWrapper onMouseLeave={(): void => setVisible(false)}>
      <StyledSearchBar
        onChange={(e) => {
          if (!visible) setVisible(true);
          setSelectedSuggestion(null);
          setInput(e.target.value);
          setKeyword(e.target.value);
        }}
        onKeyUp={onKeyUp}
        onClickInput={(): void => {
          trackManually(
            'searchBox',
            'headerBar',
            'searchProduct',
            JSON.stringify({
              searchKeyword: input ? input.trim() : '',
              ...(typeof window !== 'undefined'
                ? { href: window.location.href, url: window.location.href }
                : {}),
            }),
          );
          setVisible(true);
          setStartCallApi(true);
        }}
        value={input}
        placeholder={t('Nhập từ khoá cần tìm')}
        onClickIcon={() =>
          input && input.trim() && handleSearch({ query: input })
        }
        onKeyDown={handleNavigateSearchResult}
      />
      <SearchSuggestion
        show={
          visible &&
          (showHistories || showSuggestions || showRecommendedKeywords)
        }
      >
        {showHistories && (
          <SearchRecent
            selected={selectedSuggestion}
            histories={histories}
            onRemoveSearchHistories={removeSearchHistories}
            onSearch={handleSearch}
            onHoverItem={setSelectedSuggestion}
          />
        )}
        {showSuggestions && (
          <SearchList
            suggestions={suggestions}
            onRedirectDetailProduct={onRedirectDetailProduct}
            onHoverItem={setSelectedSuggestion}
            selected={selectedSuggestion}
            onAddToCart={addToCart}
            onSearch={handleSearch}
            isSuggestProduct={isSuggestProduct}
          />
        )}
        {showRecommendedKeywords && (
          <RecommendedKeyword
            onClick={(k) => handleSearch({ query: k })}
            popularKeywords={terms}
          />
        )}
      </SearchSuggestion>
    </SearchBarWrapper>
  );
};
